import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useSearchParams, useNavigate } from "react-router-dom";
import Footer from "../components/Layout/Footer";
import Header from "../components/Layout/Header";
import Loader from "../components/Layout/Loader";
import Sidebar from '../components/Layout/Sidebar';
import CategorySection from "../components/Shop/CategorySection";
import FeaturedShop from "../components/Shop/FeaturedShop";
import SplitImagePromo from "../components/Shop/SplitImagePromo";
import ThreeTileGrid from "../components/Shop/ThreeTileGrid";
import ShopBySellerCircle from "../components/Shop/ShopBySellerCircle";

import HeroSliderMarketplace from "../components/Route/Hero/HeroSliderMarketplace";
import RetailCategorySection from "../components/Marketplace/RetailCategorySection";
import TrendingProductsCarosel from "../components/Products/TrendingProductsCarosel";
import ShopByBrand from "../components/Brands/ShopByBrandCarosel";
import Carousel4Slides from "../components/Carousel4Slides";
import SponsoredProducts from "../components/Sponsored/SponsoredProducts";
import SponsoredBrands from "../components/Sponsored/SponsoredBrands";
import SponsoredShops from "../components/Sponsored/SponsoredShops";
import DualProductAdCarouselProps from "../components/Sponsored/DualProductAdCarouselProps";
import QuadProductAdCarouselProps from "../components/Sponsored/QuadProductCarouselProps";
import SponsoredProductCarouselProps from "../components/Sponsored/SponsoredProductCarouselProps";
import SponsoredBrandCarouselProps from "../components/Sponsored/SponsoredBrandCarouselProps";
import SponsoredShopsCarouselProps from "../components/Sponsored/SponsoredShopsCarouselProps";
import TopBannerAdProps from "../components/Sponsored/TopBannerAdProps";
import BannerAdProps from "../components/Sponsored/BannerAdProps";
import MixedSponsoredAdsProps from "../components/Sponsored/MixedSponsoredAdsProps";
import CustomAdCarousel from "../components/Sponsored/CustomAdCarousel";
import TopBannerAdPropsMobile from "../components/Sponsored/TopBannerAdPropMobile";
import BannerAdPropsMobile from "../components/Sponsored/BannerAdPropsMobile";
import useWindowSize from "../hooks/useWindowSize";
import SponsoredProductCarouselPropsMobile from "../components/Sponsored/SponsoredProductsCarouselPropsMobile";
import SponsoredBrandCarouselPropsMobile from "../components/Sponsored/SponsoredBrandCarouselPropsMobile";
import SponsoredShopsCarouselPropsMobile from "../components/Sponsored/SponsoredShopsCarouselPropsMobile";
import MarketplaceProductCarousel from "../components/Marketplace/MarketplaceProductCarousel";
import ProductInfiniteMosaic from "../components/Marketplace/ProductInfiniteMosaic";
import axios from "axios";
import { adsServer, ecommerceServer } from "../server"; 
import SingleLargeProductGrid from "../components/Marketplace/SingleLargeProductGrid";
import ShopGrid from "../components/Marketplace/ShopGrid";
import CategoryScroller from '../components/Marketplace/CategoryScroller';
import { categoriesData } from "../static/data";
import '../stylesheets/HomeMarketplaceBentoGuardrails.css';

const MarketplacePageEmptyState = ({ title, message }) => (
  <section className="marketplace-page-empty-state-panel bento-page-empty-state-panel" aria-label={title}>
    <h2 className="marketplace-page-empty-state-title">{title}</h2>
    <p className="marketplace-page-empty-state-message">{message}</p>
  </section>
);


const MarketplacePage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const category = searchParams.get("category");
  const subCategory = searchParams.get("subCategory");
  const categoryData = searchParams.get("category");
  const { allProducts, isLoading } = useSelector((state) => state.products);
  const [data, setData] = useState([]);
  const [productAds, setProductAds] = useState([]);
  const [shopAds, setShopAds] = useState([]);
  const [brandAds, setBrandAds] = useState([]);
  const [customAds, setCustomAds] = useState([]);
  const [buzzVibeProducts, setBuzzVibeProducts] = useState([]);
  const [shopImages, setShopImages] = useState([]); // New state for shop images
  const [sellers, setSellers] = useState([]); // New state for sellers
  const [hasLoadedSellers, setHasLoadedSellers] = useState(false);
  const [featuredShopId, setFeaturedShopId] = useState(null); // New state for featured shop
  const windowSize = useWindowSize();

  const handleCategorySelect = (selectedCategory) => {
    navigate(`/products/${encodeURIComponent(selectedCategory.title)}`);
  };

  const handleSubCategoryClick = (selectedSubCategory) => {
    navigate(`/products/${encodeURIComponent(category)}/${encodeURIComponent(selectedSubCategory.title)}`);
  };

  // Fetch sponsored ads (products, shops, and brands)
  useEffect(() => {
    const fetchSponsoredAds = async () => {
      try {
        const response = await axios.get(`${adsServer}/sponsored/sponsored-sidebar-ads-test`, {
          withCredentials: true,
          headers: {
            'current-profile': sessionStorage.getItem('currentProfile') || 'defaultProfile',
          }
        });
        const allAds = response.data;

        // Filter for product ads, shop ads, and brand ads
        const productAds = allAds.filter((ad) => ad.type === 'product');
        const shopAds = allAds.filter((ad) => ad.type === 'shop');
        const brandAds = allAds.filter((ad) => ad.type === 'brand');
        const customAds = allAds.filter((ad) => ad.type === 'custom');

        setProductAds(productAds);
        setShopAds(shopAds);
        setBrandAds(brandAds);
        setCustomAds(customAds);
      } catch (error) {
        console.error('Error fetching sponsored ads:', error);
      }
    };

    fetchSponsoredAds();
  }, []);

  // Fetch sellers from ecommerce server
  useEffect(() => {
    const fetchSellers = async () => {
      try {
        const response = await axios.get(`${ecommerceServer}/shop/all-sellers`, { withCredentials: true });
        const fetchedSellers = response.data.sellers || [];
        setSellers(fetchedSellers);

        // Optionally set the first seller as the featured shop
        if (fetchedSellers.length > 0) {
          setFeaturedShopId(fetchedSellers[0]._id);
        }
      } catch (error) {
        console.error('Error fetching sellers:', error);
      } finally {
        setHasLoadedSellers(true);
      }
    };
    fetchSellers();
  }, []);

  useEffect(() => {
    let filteredProducts = allProducts || [];

    // Filter for retail products
    filteredProducts = filteredProducts.filter((product) =>
      product.productType.includes('retail')
    );

    // Further filter by category if necessary
    if (categoryData) {
      filteredProducts = filteredProducts.filter(
        (product) => product.category === categoryData
      );
    }

    // Filter products from BuzzVibe Boutique using shop.handle
    const buzzVibeProducts = filteredProducts.filter(
      (product) =>
        product.shop &&
        product.shop.handle &&
        product.shop.handle.trim().toLowerCase() === 'buzzvibehollywood'
    );

    // Define shopImages manually or fetch from shop data
    // For demonstration, we'll define a static array
    const shopImages = [
      {
        url: 'https://imagedelivery.net/mEHe7vWpwEHOhne-YSqzMw/385d8f3f-e649-4b34-e3b4-cadceaa19900/public',
        alt: 'MATRIX Boutique - Image 1',
      },
      {
        url: 'https://imagedelivery.net/mEHe7vWpwEHOhne-YSqzMw/6a508243-40c8-4cbb-eaf8-042ddc025d00/public',
        alt: 'MATRIX Boutique - Image 2',
      },
      {
        url: 'https://imagedelivery.net/mEHe7vWpwEHOhne-YSqzMw/74bb0517-36a1-48ad-2eab-1b1d6a8bc600/public',
        alt: 'MATRIX Boutique - Image 3',
      },
      // Add more images as needed
    ];

    setData(filteredProducts);
    setBuzzVibeProducts(buzzVibeProducts);
    setShopImages(shopImages);
  }, [allProducts, categoryData]);

  // Select a specific product ad for TopBannerAdProps and BannerAdProps
  const topBannerAd = productAds.length > 0 ? productAds[0] : null;
  const bannerAd = productAds.length > 1 ? productAds[1] : null;

  const isMobile = windowSize.width <= 768;
  const hasLoadedProducts = !isLoading && Array.isArray(allProducts);
  const featuredProductsEmptyState = (
    <MarketplacePageEmptyState
      title="Featured Products"
      message="No featured products to display yet."
    />
  );
  const shopsEmptyState = (
    <MarketplacePageEmptyState
      title="Shops"
      message="No shops to display yet."
    />
  );
  const retailProductsEmptyState = (
    <MarketplacePageEmptyState
      title="Retail Products"
      message="No retail products found."
    />
  );

  return (
    <>
      {isLoading ? (
        <Loader />
      ) : (
        <div>
          <Header activeHeading={3} />
          <Sidebar />
          <div className="category-products-page__category-scroller">
            <CategoryScroller 
              categoriesData={categoriesData} 
              onCategorySelect={handleCategorySelect} 
              currentCategory={category}
            />
          </div>
          {category && (
              <div className="category-products-page__subcategories">
                
                <div className="subcategory-list">
                  {categoriesData.find(cat => cat.title === category)?.subCategories.map((sub) => (
                    <button 
                      key={sub.id} 
                      onClick={() => handleSubCategoryClick(sub)} 
                      className={`subcategory-button ${subCategory === sub.title ? 'active-subcategory' : ''}`}
                    >
                      {sub.title}
                    </button>
                  )) || <p>No subcategories available.</p>}
                </div>
              </div>
            )}
          <div className="marketplace-page-content">
            
              
            
            

            {/* Integrate ShopGrid Here */}
           

            {/* Grid container with left and right sides */}
            <div className="marketplace-page-grid-container">
              {/* Left container (75%) */}
              <div className="marketplace-page-left-container marketplace-page-bento-guardrail-scope">
              
                
                <HeroSliderMarketplace compact />
                <section className="shop-avatar-title-section" aria-label="Shops">
                  <div className="shop-avatar-title-bar">
                    <h3>Shops</h3>
                  </div>
                  <ShopBySellerCircle sellers={sellers} />
                </section>
                <RetailCategorySection />
                <DualProductAdCarouselProps ads={productAds} />
                <ThreeTileGrid products={buzzVibeProducts} shopImages={shopImages} />
                {hasLoadedProducts && (
                  data.length > 0 ? (
                    <SingleLargeProductGrid products={data} />
                  ) : featuredProductsEmptyState
                )}
                {hasLoadedSellers && (
                  sellers.length > 0 ? (
                    <ShopGrid shops={sellers} />
                  ) : shopsEmptyState
                )}

                {isMobile ? (
                  <SponsoredProductCarouselPropsMobile ads={productAds} />
                ) : (
                  <SponsoredProductCarouselProps ads={productAds} />
                )}

                

                <QuadProductAdCarouselProps ads={productAds} />

                {isMobile ? (
                  <SponsoredBrandCarouselPropsMobile ads={brandAds} />
                ) : (
                  <SponsoredBrandCarouselProps ads={brandAds} />
                )}

                {isMobile ? (
                  <SponsoredShopsCarouselPropsMobile ads={shopAds} />
                ) : (
                  <SponsoredShopsCarouselProps ads={shopAds} />
                )}
                
                <br />
                <br />
                <MarketplaceProductCarousel data={data} emptyState={hasLoadedProducts ? retailProductsEmptyState : null} />
                

               
                
              </div>

              {/* Right container (25%) */}
              <div className="marketplace-page-right-container">
                <MixedSponsoredAdsProps ads={[...productAds, ...brandAds, ...shopAds]} />
              </div>
            </div>

            <Footer />
          </div>
        </div>
      )}
    </>
  );
};

export default MarketplacePage;



